import React, { useRef, useCallback, useMemo } from 'react'
import styles from './ColorWheel.module.scss'

const SIZE = 160
const RADIUS = SIZE / 2

function hexToRgb(hex) {
  const clean = (hex || '#000000').replace('#', '')
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean
  const num = parseInt(full, 16)
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')
}

// Standard RGB <-> HSV conversions (h in [0,360), s/v in [0,1])
function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const s = max === 0 ? 0 : d / max
  return { h, s, v: max }
}

function hsvToRgb(h, s, v) {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let r, g, b
  if (h < 60) { [r, g, b] = [c, x, 0] } else if (h < 120) { [r, g, b] = [x, c, 0] }
  else if (h < 180) { [r, g, b] = [0, c, x] } else if (h < 240) { [r, g, b] = [0, x, c] }
  else if (h < 300) { [r, g, b] = [x, 0, c] } else { [r, g, b] = [c, 0, x] }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 }
}

/**
 * ColorWheel - a circular hue/saturation picker (angle = hue, distance from
 * center = saturation) with a separate brightness slider underneath, as an
 * alternative to the square saturation/value grid pickers (ChromePicker etc).
 */
export default function ColorWheel({ color, onChange }) {
  const wheelRef = useRef(null)
  const draggingRef = useRef(false)

  const { h, s, v } = useMemo(() => {
    const { r, g, b } = hexToRgb(color)
    return rgbToHsv(r, g, b)
  }, [color])

  const emitFromAngleRadius = useCallback((angleDeg, radiusRatio) => {
    const { r, g, b } = hsvToRgb(angleDeg, radiusRatio, v)
    onChange(rgbToHex(r, g, b))
  }, [onChange, v])

  const emitFromValue = useCallback((newV) => {
    const { r, g, b } = hsvToRgb(h, s, newV)
    onChange(rgbToHex(r, g, b))
  }, [onChange, h, s])

  const pointToHueSat = useCallback((clientX, clientY) => {
    const rect = wheelRef.current.getBoundingClientRect()
    const dx = clientX - (rect.left + RADIUS)
    const dy = clientY - (rect.top + RADIUS)
    const dist = Math.sqrt(dx * dx + dy * dy)
    const radiusRatio = Math.min(1, dist / RADIUS)
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI
    if (angle < 0) angle += 360
    return { angle, radiusRatio }
  }, [])

  const handleWheelPointerDown = useCallback((e) => {
    draggingRef.current = true
    const { angle, radiusRatio } = pointToHueSat(e.clientX, e.clientY)
    emitFromAngleRadius(angle, radiusRatio)

    const handleMove = (moveEvent) => {
      if (!draggingRef.current) return
      const next = pointToHueSat(moveEvent.clientX, moveEvent.clientY)
      emitFromAngleRadius(next.angle, next.radiusRatio)
    }
    const handleUp = () => {
      draggingRef.current = false
      document.removeEventListener('pointermove', handleMove)
      document.removeEventListener('pointerup', handleUp)
    }
    document.addEventListener('pointermove', handleMove)
    document.addEventListener('pointerup', handleUp)
  }, [pointToHueSat, emitFromAngleRadius])

  // Puck position: angle = hue, distance from center = saturation
  const puckAngleRad = (h * Math.PI) / 180
  const puckDist = s * RADIUS
  const puckX = RADIUS + puckDist * Math.cos(puckAngleRad)
  const puckY = RADIUS + puckDist * Math.sin(puckAngleRad)

  return (
    <div className={styles.wheelContainer}>
      <div
        ref={wheelRef}
        className={styles.wheel}
        style={{ width: SIZE, height: SIZE }}
        onPointerDown={handleWheelPointerDown}
      >
        <div
          className={styles.puck}
          style={{ left: puckX, top: puckY, backgroundColor: color || '#000000' }}
        />
      </div>
      <div className={styles.valueRow}>
        <span className={styles.valueLabel}>Brightness</span>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={Math.round(v * 100)}
          onChange={(e) => emitFromValue(Number(e.target.value) / 100)}
          className={styles.valueSlider}
        />
      </div>
    </div>
  )
}
